import { useEffect, useRef } from 'react'

/**
 * useEntityDeepLink - Opens a marker popup from a ?entity=<uuid> deeplink
 * (e.g. arriving from a city landing page), centering it above the bottom sheet.
 * @atomic
 */
export function useEntityDeepLink({ entities, mapRef, markerRefs, currentOpenPopupRef, panelState, handlePanelStateChange }) {
  const entityDeepLinkAppliedRef = useRef(false)
  // Refs so the timer callbacks below always read current values without being in deps
  const panelStateRef = useRef(panelState)
  const handlePanelStateChangeRef = useRef(handlePanelStateChange)
  panelStateRef.current = panelState
  handlePanelStateChangeRef.current = handlePanelStateChange

  useEffect(() => {
    if (entityDeepLinkAppliedRef.current) return
    const entityId = new URLSearchParams(window.location.search).get('entity')
    if (!entityId || !entities?.length) return

    // entities are sighting markers: id=sighting.id, entityId=entity.id, coords=currentLatitude/Lng
    const target = entities.find(e => e.entityId === entityId && e.isLatest)
      || entities.find(e => e.entityId === entityId)
    if (!target) return

    entityDeepLinkAppliedRef.current = true

    // Give markers time to mount after entities load.
    // No cleanup returned — once we've found the target and committed to opening the
    // popup, React must NOT cancel these timers when entities re-fetch (which happens
    // multiple times on load). The entityDeepLinkAppliedRef guard prevents re-entry.
    setTimeout(() => {
      const marker = markerRefs?.current?.[target.id]
      if (!marker || !mapRef.current) return

      const map = mapRef.current

      const openCentered = () => {
        marker.openPopup()
        // Register in currentOpenPopupRef so subsequent marker clicks can close this popup
        if (currentOpenPopupRef) {
          currentOpenPopupRef.current = marker
        }
        // Reposition map so the marker sits in the upper portion of the visible area,
        // leaving room below it for the bottom sheet. Use project/unproject for correct math.
        setTimeout(() => {
          if (!map) return
          const mapSize = map.getSize()
          const sheetHeight = 80 // minimized panel height at bottom
          const zoom = map.getZoom()
          // We want the marker at 35% from top of screen (well above the bottom sheet)
          const targetY = (mapSize.y - sheetHeight) * 0.35
          // After setView the marker is at mapSize.y/2. Compute the new center that places
          // the marker at targetY: newCenter = markerPixel shifted down by (mapSize.y/2 - targetY)
          const markerPixel = map.project([target.currentLatitude, target.currentLongitude], zoom)
          const newCenterPixel = markerPixel.subtract([0, targetY - mapSize.y / 2])
          const newCenter = map.unproject(newCenterPixel, zoom)
          map.panTo(newCenter, { animate: true, duration: 0.4 })
        }, 50)
      }

      // Suppress all moveend events from the setView + panTo + panel animation
      map._suppressBoundsUntil = Date.now() + 2000
      map.setView([target.currentLatitude, target.currentLongitude], 16, { animate: false })

      // Read panelState from ref so we always get the current value (not a stale closure)
      const currentPanelState = panelStateRef.current
      const currentHandlePanelStateChange = handlePanelStateChangeRef.current

      // Minimize the immersive panel first so the popup isn't obstructed, then open
      if (currentPanelState !== 'minimized') {
        currentHandlePanelStateChange('minimized')
        // Wait for the panel collapse animation before opening the popup
        setTimeout(openCentered, 350)
      } else {
        openCentered()
      }
    }, 300)
  // Only re-run when entities or marker refs change — panelState/handlePanelStateChange
  // are read via refs above to avoid cancelling the in-progress timers on panel state changes
  }, [entities, mapRef, markerRefs])
}
